/**
 * Screen Recording Service
 * Handles screen recording using MediaRecorder API for bug reporting
 */

import { ref, computed, type Ref } from 'vue';
import { isIOSLike } from '@/utils/platformDetection';

export interface RecordingOptions {
  maxDuration?: number; // in milliseconds
  mimeType?: string;
  videoBitsPerSecond?: number;
}

export interface SystemInfo {
  userAgent: string;
  platform: string;
  screenResolution: string;
  viewportSize: string;
  timestamp: string;
  url: string;
}

export interface ApiCallRecord {
  timestamp: string;
  method: string;
  url: string;
  status: number;
  duration: number;
  requestHeaders?: Record<string, string>;
  requestBody?: any;
  responseStatus: number;
  responseBody?: any;
  error?: string;
}

type RecordingState = 'inactive' | 'countdown' | 'recording' | 'paused' | 'stopped';

class ScreenRecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private recordingTimer: number | null = null;
  private maxDurationTimer: number | null = null;
  private countdownTimer: number | null = null;
  private apiCalls: ApiCallRecord[] = [];
  private recordingStartTime: number = 0;

  // Reactive state
  public state: Ref<RecordingState> = ref('inactive');
  public duration: Ref<number> = ref(0); // in seconds
  public recordedBlob: Ref<Blob | null> = ref(null);
  public error: Ref<string | null> = ref(null);
  public countdownValue: Ref<number> = ref(3); // countdown from 3

  // Default options
  private defaultOptions: RecordingOptions = {
    maxDuration: 5 * 60 * 1000, // 5 minutes
    mimeType: 'video/webm;codecs=vp9',
    videoBitsPerSecond: 2500000, // 2.5 Mbps
  };

  /**
   * Check if screen recording is supported
   */
  public isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
      typeof MediaRecorder !== 'undefined' &&
      !isIOSLike()
    );
  }

  /**
   * Get supported MIME type
   */
  private getSupportedMimeType(): string {
    const types = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm'; // fallback
  }

  /**
   * Start screen recording with an existing stream
   */
  public async startRecordingWithStream(stream: MediaStream, options: RecordingOptions = {}): Promise<void> {
    if (!this.isSupported()) {
      this.error.value = 'Screen recording is not supported in your browser';
      throw new Error(this.error.value);
    }

    // Merge options with defaults
    const recordingOptions = { ...this.defaultOptions, ...options };

    // Use supported MIME type
    recordingOptions.mimeType = this.getSupportedMimeType();

    try {
      this.stream = stream;

      // Initialize MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: recordingOptions.mimeType,
        videoBitsPerSecond: recordingOptions.videoBitsPerSecond,
      });

      // Reset state
      this.recordedChunks = [];
      this.recordedBlob.value = null;
      this.duration.value = 0;
      this.error.value = null;

      // Handle data available
      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        console.error('🎥 DATA AVAILABLE:', {
          hasData: !!event.data,
          dataSize: event.data?.size || 0,
          chunksCount: this.recordedChunks.length,
        });
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
          console.error('🎥 CHUNK ADDED, total chunks:', this.recordedChunks.length);
        } else {
          console.error('🎥 WARNING: ondataavailable but no data or size is 0');
        }
      };

      // Handle recording stop
      this.mediaRecorder.onstop = () => {
        console.error('🎥 ONSTOP TRIGGERED, chunks:', this.recordedChunks.length);
        this.recordedBlob.value = new Blob(this.recordedChunks, {
          type: recordingOptions.mimeType,
        });
        console.error('🎥 BLOB CREATED:', {
          size: this.recordedBlob.value?.size,
          type: this.recordedBlob.value?.type,
        });

        // Force trigger watchers by setting state AFTER blob
        console.error('🎥 SETTING STATE TO STOPPED');
        this.state.value = 'stopped';

        // Use nextTick to ensure Vue reactivity has processed the blob change
        import('vue').then(({ nextTick }) => {
          nextTick(() => {
            console.error('🎥 NEXTTICK: State and blob should now be reactive');
          });
        });

        this.stopTimers();
        this.stopStream();
        console.error('🎥 ONSTOP COMPLETE, state:', this.state.value);
      };

      // Handle errors
      this.mediaRecorder.onerror = (event: Event) => {
        console.error('MediaRecorder error:', event);
        this.error.value = 'Recording error occurred';
        this.stopRecording();
      };

      // Handle stream end (e.g., user stops sharing)
      this.stream.getTracks().forEach((track) => {
        track.onended = () => {
          if (this.state.value === 'recording') {
            this.stopRecording();
          }
        };
      });

      // Start recording
      this.mediaRecorder.start(1000); // Collect data every second
      this.state.value = 'recording';
      this.recordingStartTime = Date.now();
      this.apiCalls = []; // Clear previous API calls

      // Start duration timer
      this.startDurationTimer();

      // Set max duration timer
      if (recordingOptions.maxDuration) {
        this.maxDurationTimer = window.setTimeout(() => {
          this.stopRecording();
        }, recordingOptions.maxDuration);
      }
    } catch (err: any) {
      console.error('Failed to start recording:', err);
      this.error.value = err.message || 'Failed to start screen recording';
      this.cleanup();
      throw err;
    }
  }

  /**
   * Start countdown before recording
   */
  public async startCountdown(options: RecordingOptions = {}): Promise<void> {
    try {
      this.error.value = null;

      // Request screen capture first
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      if (!stream) {
        throw new Error('Screen capture was cancelled');
      }

      // Store stream temporarily
      this.stream = stream;

      // Start countdown
      this.state.value = 'countdown';
      this.countdownValue.value = 3;

      this.countdownTimer = window.setInterval(() => {
        this.countdownValue.value--;

        if (this.countdownValue.value <= 0) {
          if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
          }
          // Start actual recording
          // Note: Don't clear this.stream here - startRecordingWithStream needs it
          this.startRecordingWithStream(this.stream!, options);
        }
      }, 1000);
    } catch (error: any) {
      this.cancelCountdown();
      this.error.value = error.message || 'Failed to access screen';
      throw error;
    }
  }

  /**
   * Cancel countdown
   */
  public cancelCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.state.value = 'inactive';
    this.countdownValue.value = 3;

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  /**
   * Start screen recording (requests screen capture then starts recording)
   * Use startCountdown() for countdown behavior
   */
  public async startRecording(options: RecordingOptions = {}): Promise<void> {
    try {
      // Request screen capture
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });

      if (!stream) {
        throw new Error('Screen capture was cancelled');
      }

      await this.startRecordingWithStream(stream, options);
    } catch (error: any) {
      this.error.value = error.message || 'Failed to start recording';
      throw error;
    }
  }

  /**
   * Stop screen recording
   */
  public stopRecording(): void {
    console.log('🎥 screenRecordingService: stopRecording called, current state:', this.state.value);
    console.log('🎥 screenRecordingService: mediaRecorder exists:', !!this.mediaRecorder);
    if (this.mediaRecorder && this.state.value === 'recording') {
      console.log('🎥 screenRecordingService: calling mediaRecorder.stop()');
      this.mediaRecorder.stop();
    } else {
      console.warn('🎥 screenRecordingService: cannot stop - either no mediaRecorder or not recording');
    }
  }

  /**
   * Pause recording
   */
  public pauseRecording(): void {
    if (this.mediaRecorder && this.state.value === 'recording') {
      this.mediaRecorder.pause();
      this.state.value = 'paused';
      this.stopTimers();
    }
  }

  /**
   * Resume recording
   */
  public resumeRecording(): void {
    if (this.mediaRecorder && this.state.value === 'paused') {
      this.mediaRecorder.resume();
      this.state.value = 'recording';
      this.startDurationTimer();
    }
  }

  /**
   * Cancel recording
   */
  public cancelRecording(): void {
    this.stopTimers();
    this.stopStream();
    this.recordedChunks = [];
    this.recordedBlob.value = null;
    this.duration.value = 0;
    this.state.value = 'inactive';
    this.error.value = null;
  }

  /**
   * Get recorded video blob
   */
  public getRecordedBlob(): Blob | null {
    return this.recordedBlob.value;
  }

  /**
   * Get video blob URL for preview
   */
  public getVideoUrl(): string | null {
    if (this.recordedBlob.value) {
      return URL.createObjectURL(this.recordedBlob.value);
    }
    return null;
  }

  /**
   * Get recording duration in formatted time
   */
  public getFormattedDuration(): string {
    const minutes = Math.floor(this.duration.value / 60);
    const seconds = this.duration.value % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Get file size in human-readable format
   */
  public getFileSize(): string {
    if (!this.recordedBlob.value) return '0 KB';

    const bytes = this.recordedBlob.value.size;
    const kb = bytes / 1024;
    const mb = kb / 1024;

    if (mb >= 1) {
      return `${mb.toFixed(2)} MB`;
    }
    return `${kb.toFixed(2)} KB`;
  }

  /**
   * Collect system information for bug report
   */
  public getSystemInfo(): SystemInfo {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };
  }

  /**
   * Download recorded video
   */
  public downloadRecording(filename: string = 'screen-recording.webm'): void {
    if (!this.recordedBlob.value) return;

    const url = this.getVideoUrl();
    if (!url) return;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    // Cleanup
    URL.revokeObjectURL(url);
  }

  /**
   * Start duration timer
   */
  private startDurationTimer(): void {
    this.recordingTimer = window.setInterval(() => {
      this.duration.value++;
    }, 1000);
  }

  /**
   * Stop all timers
   */
  private stopTimers(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
      this.maxDurationTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /**
   * Stop media stream
   */
  private stopStream(): void {
    if (this.stream) {
      console.error('🎥 STOPPING STREAM, tracks:', this.stream.getTracks().length);
      this.stream.getTracks().forEach((track) => {
        console.error('🎥 Stopping track:', track.kind, track.readyState);
        track.stop();
        console.error('🎥 Track stopped, new state:', track.readyState);
      });
      this.stream = null;
      console.error('🎥 STREAM STOPPED AND CLEARED');
    } else {
      console.error('🎥 NO STREAM TO STOP');
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.stopTimers();
    this.stopStream();
    this.mediaRecorder = null;
    this.state.value = 'inactive';
  }

  /**
   * Reset service state
   */
  public reset(): void {
    this.cleanup();
    this.recordedChunks = [];
    this.recordedBlob.value = null;
    this.duration.value = 0;
    this.error.value = null;
    this.apiCalls = [];
    this.recordingStartTime = 0;
  }

  /**
   * Track an API call (called by apiClient interceptor)
   */
  public trackApiCall(record: ApiCallRecord): void {
    // Only track if we're recording
    if (this.state.value === 'recording') {
      this.apiCalls.push(record);
    }
  }

  /**
   * Get recorded API calls
   */
  public getApiCalls(): ApiCallRecord[] {
    return [...this.apiCalls];
  }

  /**
   * Check if currently recording (for apiClient to know when to track)
   */
  public isCurrentlyRecording(): boolean {
    return this.state.value === 'recording';
  }

  /**
   * Get recording start time
   */
  public getRecordingStartTime(): number {
    return this.recordingStartTime;
  }
}

// Export singleton instance
export const screenRecordingService = new ScreenRecordingService();
export default screenRecordingService;
